/**
 * Shipping Policy Page
 *
 * Detailed shipping information for PetMas customers.
 * Includes zones, rates, delivery times, and policies.
 */

import { Link } from 'react-router-dom';

const LIMA_DISTRICTS = [
  'Miraflores', 'San Isidro', 'Surco', 'San Borja', 'La Molina', 'Barranco',
  'Jesus Maria', 'Lince', 'Magdalena', 'Pueblo Libre', 'San Miguel', 'Surquillo',
  'Chorrillos', 'Ate', 'Santa Anita', 'La Victoria', 'Lima Centro', 'Breña',
  'Rimac', 'San Juan de Lurigancho', 'Los Olivos', 'Independencia', 'Comas',
  'San Martin de Porres', 'Callao', 'Ventanilla', 'Villa El Salvador', 'Villa Maria del Triunfo',
];

const PROVINCE_COVERAGE = [
  { region: 'Costa Norte', cities: 'Trujillo, Chiclayo, Piura, Tumbes, Chimbote' },
  { region: 'Costa Sur', cities: 'Arequipa, Tacna, Ica, Nazca, Ilo' },
  { region: 'Sierra', cities: 'Cusco, Huancayo, Ayacucho, Cajamarca, Puno' },
  { region: 'Selva', cities: 'Iquitos, Tarapoto, Pucallpa' },
];

export function ShippingPolicyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary to-primary-dark text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <TruckIcon />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Politica de Envios
          </h1>
          <p className="text-lg md:text-xl opacity-90 max-w-2xl mx-auto">
            Llevamos el mejor alimento y accesorios para tu mascota a todo el Peru.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Free Shipping Banner */}
          <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6 mb-12 text-center">
            <div className="text-4xl mb-2">🚚</div>
            <h2 className="text-2xl font-bold text-green-800 mb-2">
              ¡Envio GRATIS en Lima!
            </h2>
            <p className="text-green-700">
              En pedidos mayores a <span className="font-bold">S/100</span> dentro de Lima Metropolitana
            </p>
          </div>

          {/* Lima Section */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary text-xl">
                🏙️
              </span>
              Lima Metropolitana
            </h2>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Monto del Pedido
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Costo de Envio
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Tiempo de Entrega
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="px-6 py-4 text-gray-700">Mayor a S/100</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        GRATIS
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-700">24-48 horas habiles</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-700">Menor a S/100</td>
                    <td className="px-6 py-4 font-medium text-gray-900">S/10.00</td>
                    <td className="px-6 py-4 text-gray-700">24-48 horas habiles</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-700">Envio Express</td>
                    <td className="px-6 py-4 font-medium text-gray-900">S/20.00</td>
                    <td className="px-6 py-4 text-gray-700">Mismo dia (antes 12pm)</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6">
              <h3 className="font-semibold text-gray-900 mb-3">Distritos con cobertura:</h3>
              <div className="flex flex-wrap gap-2">
                {LIMA_DISTRICTS.map((district) => (
                  <span
                    key={district}
                    className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                  >
                    {district}
                  </span>
                ))}
              </div>
            </div>
          </section>

          {/* Provinces Section */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary text-xl">
                🌎
              </span>
              Envios a Provincias
            </h2>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Peso del Pedido
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Costo Aproximado
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Tiempo de Entrega
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="px-6 py-4 text-gray-700">Hasta 5 kg</td>
                    <td className="px-6 py-4 font-medium text-gray-900">S/15 - S/25</td>
                    <td className="px-6 py-4 text-gray-700">3-5 dias habiles</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-700">5 - 15 kg</td>
                    <td className="px-6 py-4 font-medium text-gray-900">S/25 - S/45</td>
                    <td className="px-6 py-4 text-gray-700">3-5 dias habiles</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-700">15 - 30 kg</td>
                    <td className="px-6 py-4 font-medium text-gray-900">S/45 - S/70</td>
                    <td className="px-6 py-4 text-gray-700">4-6 dias habiles</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-700">Mas de 30 kg</td>
                    <td className="px-6 py-4 font-medium text-gray-900">Cotizar</td>
                    <td className="px-6 py-4 text-gray-700">5-7 dias habiles</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {PROVINCE_COVERAGE.map((zone) => (
                <div
                  key={zone.region}
                  className="bg-gray-50 rounded-lg p-4"
                >
                  <h4 className="font-semibold text-gray-900 mb-1">{zone.region}</h4>
                  <p className="text-sm text-gray-600">{zone.cities}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Tracking Section */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary text-xl">
                📍
              </span>
              Seguimiento de Pedidos
            </h2>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-blue-600 font-bold">1</span>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Pedido Confirmado</h4>
                  <p className="text-sm text-gray-600">
                    Recibiras un email de confirmacion con los detalles de tu pedido.
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-yellow-600 font-bold">2</span>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">En Preparacion</h4>
                  <p className="text-sm text-gray-600">
                    Tu pedido esta siendo preparado en nuestro almacen.
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-green-600 font-bold">3</span>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">En Camino</h4>
                  <p className="text-sm text-gray-600">
                    Recibiras el codigo de rastreo para seguir tu envio en tiempo real.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Important Info */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary text-xl">
                ⚠️
              </span>
              Informacion Importante
            </h2>

            <div className="space-y-4">
              <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
                <h4 className="font-semibold text-amber-800 mb-1">Horario de Despacho</h4>
                <p className="text-amber-700 text-sm">
                  Los pedidos realizados antes de las 2:00 PM se despachan el mismo dia.
                  Pedidos posteriores se procesan al siguiente dia habil.
                </p>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
                <h4 className="font-semibold text-blue-800 mb-1">Verificacion de Direccion</h4>
                <p className="text-blue-700 text-sm">
                  Asegurate de ingresar correctamente tu direccion de entrega.
                  Incluye referencias para facilitar la ubicacion.
                </p>
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-4 rounded-r-lg">
                <h4 className="font-semibold text-purple-800 mb-1">Recepcion del Pedido</h4>
                <p className="text-purple-700 text-sm">
                  Es necesario que alguien reciba el pedido en la direccion indicada.
                  Si no hay nadie, se intentara una segunda entrega.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
                <h4 className="font-semibold text-red-800 mb-1">Productos Pesados</h4>
                <p className="text-red-700 text-sm">
                  Para sacos de alimento de 15kg o mas, el repartidor solo llevara
                  el producto hasta la puerta del edificio/casa.
                </p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <div className="bg-gray-50 rounded-2xl p-8 text-center">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              ¿Tienes alguna consulta sobre tu envio?
            </h3>
            <p className="text-gray-600 mb-6">
              Nuestro equipo esta listo para ayudarte.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/ayuda"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors"
              >
                Centro de Ayuda
              </Link>
              <a
                href="https://wa.me/51999999999"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 transition-colors"
              >
                <WhatsAppIcon />
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TruckIcon() {
  return (
    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}
